import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { NavLink } from "react-router-dom";
import { FaBars, FaTimes } from "react-icons/fa";

/* ------------------ Styles ------------------ */

const DesktopBox = styled.aside`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
  padding: 12px;

  position: sticky;
  top: 88px; /* justera efter header-höjd */
  align-self: start;

  @media (max-width: 900px) {
    display: none;
  }
`;

const MobileBar = styled.div`
  display: none;
  @media (max-width: 900px) {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }
`;

const OpenBtn = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 950;
  border-radius: 14px;
  padding: 10px 12px;
  cursor: pointer;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.06);

  display: inline-flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(12px);
  padding: 16px;

  display: flex;
  justify-content: flex-end;
`;

const Panel = styled.aside`
  width: min(420px, 92vw);
  height: calc(100vh - 32px);
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 22px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.12);
  overflow: hidden;

  display: flex;
  flex-direction: column;

  /* slide-in */
  animation: slideIn 160ms ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(12px);
      opacity: 0.6;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const PanelTop = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const PanelTitle = styled.div`
  font-weight: 950;
  color: #111827;
`;

const CloseBtn = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  border-radius: 14px;
  padding: 10px 11px;
  cursor: pointer;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }
`;

const PanelBody = styled.div`
  padding: 12px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  display: grid;
  gap: 8px;
`;

const Item = styled(NavLink)`
  display: block;
  padding: 12px 12px;
  border-radius: 14px;
  text-decoration: none;
  font-weight: 950;
  color: #111827;
  border: 1px solid transparent;

  &:hover {
    background: #f9fafb;
  }

  &.active {
    background: rgba(15, 118, 110, 0.12);
    color: #0f766e;
    border-color: rgba(15, 118, 110, 0.18);
  }
`;

/* ------------------ Component ------------------ */

const LINKS = [
  { to: "/admin", label: "Översikt" },
  { to: "/admin/opening-hours", label: "Öppettider" },
  { to: "/admin/avgifter", label: "Avgifter" },
  { to: "/admin/products", label: "Produkter" },
  { to: "/admin/news", label: "Nyheter" },
  { to: "/admin/gallery", label: "Galleri" },
  { to: "/admin/competitions", label: "Tävlingar" },
  { to: "/admin/banor", label: "Banor" },
  { to: "/admin/sponsors", label: "Sponsorer" },
  { to: "/admin/regler", label: "Regler" },
  { to: "/admin/forening", label: "Föreningen" },
  { to: "/admin/intyg", label: "Intyg" },
  { to: "/admin/kontakt", label: "Kontakt" },
];

export default function Sidebar() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  const close = () => setOpen(false);

  // Lås scroll när panelen är öppen
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  // ESC stänger
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Fokus på panelen när den öppnas (nice-to-have)
  useEffect(() => {
    if (open) panelRef.current?.focus?.();
  }, [open]);

  return (
    <>
      {/* Mobil: öppna-knapp */}
      <MobileBar>
        <OpenBtn type="button" onClick={() => setOpen(true)} aria-label="Öppna admin-meny">
          <FaBars /> Admin-meny
        </OpenBtn>
      </MobileBar>

      {/* Desktop: klassisk sidebar */}
      <DesktopBox aria-label="Adminmeny (desktop)">
        {LINKS.map((l) => (
          <Item key={l.to} to={l.to}>
            {l.label}
          </Item>
        ))}
      </DesktopBox>

      {/* Mobil: slide-in från höger */}
      {open && (
        <Overlay
          role="dialog"
          aria-modal="true"
          aria-label="Admin-meny"
          onMouseDown={(e) => {
            // klick på overlay stänger
            if (e.target === e.currentTarget) close();
          }}
        >
          <Panel ref={panelRef} tabIndex={-1} onMouseDown={(e) => e.stopPropagation()}>
            <PanelTop>
              <PanelTitle>Admin</PanelTitle>
              <CloseBtn type="button" onClick={close} aria-label="Stäng meny">
                <FaTimes />
              </CloseBtn>
            </PanelTop>

            <PanelBody>
              {LINKS.map((l) => (
                <Item key={l.to} to={l.to} onClick={close}>
                  {l.label}
                </Item>
              ))}
            </PanelBody>
          </Panel>
        </Overlay>
      )}
    </>
  );
}








